import type Luminary from '@lumen5/luminary';

import { type EnhancedToolResult } from '../types';

import { unplannedTextFinding } from './planAdherence';

import type { ScenePlan } from 'js/src/data/html-design';
import { checkpointVideo } from 'js/src/data/video-data';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import { saveRawLuminary } from 'js/src/luminary/utils';

import { getCompositionLintFindings } from './lint';
import type { LintFinding } from './lint';

/** Per-composition, since a long build has no later boundary at which to save earlier graphics. */
export function checkpointDesign(
    luminary: Luminary,
    dispatch: AppDispatch,
    parentActionGroup: string | undefined,
): void {
    dispatch(
        checkpointVideo({
            video_data: saveRawLuminary(luminary),
            systemTriggered: false,
            parentActionGroup,
        }),
    );
}

/**
 * The error set each design run last reported, so a repeat can be called out. Keyed per conversation
 * as well as per asset: a run that ends with errors leaves them in the document, so the next run's
 * first lint reproduces that set and must not be told it already tried this.
 */
const lastErrorSignatureByRun = new Map<string, string>();

// How many warnings to quote in full, and how many of those one rule may claim. A composition
// commonly trips 30+ warnings, so quoting them all would cost more than the edit being reported —
// but quoting none leaves the agent unable to act on any of them, or to judge whether a rule is even
// firing correctly.
const WARNINGS_QUOTED_MAX = 8;
const WARNINGS_QUOTED_PER_RULE_MAX = 2;

/**
 * A readable digest of the non-blocking findings: a message from every rule that fired, a second
 * example where the budget allows, then a count of what was left out.
 *
 * Breadth first, and that order matters. Findings arrive grouped in rule-declaration order, so filling
 * straight to the per-rule cap spends the budget on the earliest rules and leaves the later ones
 * named-only — the state quoting messages exists to replace, decided by a rule's position rather than
 * by how useful its finding is.
 */
export function summarizeWarnings(warnings: LintFinding[]): string {
    if (!warnings.length) {
        return '';
    }
    const quotedPerRule = new Map<string, number>();
    const quoted = new Set<LintFinding>();
    const fill = (perRuleLimit: number): void => {
        for (const warning of warnings) {
            if (quoted.size >= WARNINGS_QUOTED_MAX) return;
            if (quoted.has(warning)) continue;
            const alreadyQuoted = quotedPerRule.get(warning.ruleId) ?? 0;
            if (alreadyQuoted >= perRuleLimit) continue;
            quotedPerRule.set(warning.ruleId, alreadyQuoted + 1);
            quoted.add(warning);
        }
    };
    fill(1);
    fill(WARNINGS_QUOTED_PER_RULE_MAX);

    const lead =
        `${warnings.length} advisory warning(s) — none block. Fix the ones you agree are real; ` +
        `ignore the rest.`;
    // Read out in the findings' own order, so a breadth-first selection still reads grouped by rule.
    const list = warnings
        .filter((f) => quoted.has(f))
        .map((f) => `- [${f.ruleId}] ${f.message}`)
        .join('\n');
    // The caps skip findings, so the quoted set isn't a prefix of the list — the omitted ones have to
    // be derived by exclusion or the tally names the wrong rules.
    const omittedRules = [...new Set(warnings.filter((f) => !quoted.has(f)).map((f) => f.ruleId))];
    const omitted = warnings.length - quoted.size;
    if (omitted === 0) {
        return `${lead}\n${list}`;
    }
    return `${lead}\n${list}\n- …and ${omitted} more (${omittedRules.join(', ')}).`;
}

/**
 * Writes `html` onto the asset and re-lints, formatting the findings into a tool result. Lint errors
 * are surfaced as a failure (the composition is still committed so the preview reflects the broken
 * state and the agent can fix it); warnings are noted but still succeed. Shared by the write and
 * edit tools so both apply and report identically.
 *
 * `sceneDurationMs` is the playback duration (milliseconds, our default unit) of the scene the
 * graphic is on — passed through so the reading-time lint can judge whether text stays up long
 * enough. The composition timeline is normalized, so this real duration can only come from the scene.
 *
 * `conversationId` scopes the repeated-error detection to the design run making the calls.
 */
export async function applyHtmlAndLint(
    luminary: Luminary,
    asset: HtmlAsset,
    html: string,
    sceneDurationMs: number,
    conversationId: string | undefined,
    plan: ScenePlan | null = null,
): Promise<EnhancedToolResult> {
    const runKey = `${conversationId ?? 'no-conversation'}:${asset.id}`;
    luminary.batchRun(() => {
        asset.html = html;
    });

    const findings = await getCompositionLintFindings(asset, sceneDurationMs);
    const warnings = findings.filter((finding) => finding.severity === 'warning');

    // Not a Luminary rule because the plan never reaches the renderer, but reported with the lint
    // errors so it blocks the same way.
    const errors = findings.filter((finding) => finding.severity === 'error');
    const unplanned = unplannedTextFinding(html, plan);
    if (unplanned) {
        errors.push({ ruleId: 'text-not-in-plan', severity: 'error', message: unplanned });
    }

    if (errors.length > 0) {
        const list = errors.map((f) => `- [${f.ruleId}] ${f.message}`).join('\n');
        const signature = errors.map((f) => `${f.ruleId}|${f.message}`).join('\n');
        const repeated = lastErrorSignatureByRun.get(runKey) === signature;
        lastErrorSignatureByRun.set(runKey, signature);
        const lead = repeated
            ? `Applied, but all ${errors.length} lint error(s) are unchanged from your previous attempt — that edit did not affect them. Read the offending markup and change your approach instead of resending an equivalent fix:`
            : `Applied, but the composition has ${errors.length} lint error(s) that must be fixed:`;
        return {
            result: `${lead}\n${list}`,
            isSuccess: false,
        };
    }

    lastErrorSignatureByRun.delete(runKey);
    const warningNote = summarizeWarnings(warnings);
    return {
        result: warningNote
            ? `Successfully updated the design. ${warningNote}`
            : 'Successfully updated the design.',
        isSuccess: true,
    };
}
